import { Component, Output, EventEmitter, Input, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { KycgatewayService } from '../../kyc/kycgateway.service';
import { ApplicationDetailsService } from '../../loan-applications/loan-application-details/loan-application-details.service';
import { UserKYCService } from '../../kyc/userkyc.service';
import { UserLoginService } from 'src/app/login/userlogin.service';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom } from 'rxjs';
import { UserKYCDTO, UserKYCUpdateRequestDTO } from '../../kyc/userkyc.interface';
import { LoanApplicationItem, LoanApplicationUpdateDTO } from '../../loan-applications/loan-applications.interface';
import { LoanApplicationService } from '../../loan-applications/loan-applications.service';
import { Scoreme_AadhaarOtpVerificationResponseDataObject } from '../../kyc/Kyc.interface';
import { LosStateService } from '../los-state.service';
import { DocumentExtractionService } from 'src/app/Common/DocumentExtractionService/document-extraction.service';

@Component({
  selector: 'app-user-kyc',
  templateUrl: './user-kyc.component.html',
  styleUrls: ['./user-kyc.component.css']
})
export class UserKycComponent implements OnInit {
  @Input() applicationId: number | null = null;
  @Output() next = new EventEmitter<void>();
  @Output() prev = new EventEmitter<void>();

  private kycService = inject(KycgatewayService);
  private applicationDetailsService = inject(ApplicationDetailsService);
  private losState = inject(LosStateService);
  private toastr = inject(ToastrService);
  private destroyRef = inject(DestroyRef);
  private userKYCService = inject(UserKYCService);
  private loanApplicationService = inject(LoanApplicationService);
  private userLoginService = inject(UserLoginService);
  private documentExtractionService = inject(DocumentExtractionService);
  isExtractingDoc = false;
  isExtractingPan = false;
  isExtractingAadhaar = false;

  // ---------- PAN Document Upload ----------
  onPanDocumentUpload(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;
    const file = input.files[0];
    this.isExtractingPan = true;
    this.documentExtractionService.extractDocument(file, 'PAN', '').subscribe({
      next: (res) => {
        this.isExtractingPan = false;
        if (res && res.data) {
          const payload = res.data;
          const data = payload.data || {};
          
          this.panNumber = data.PanNumber || data.panNumber || payload.panNumber || payload.PanNumber || this.panNumber;
          this.panName = data.Name || data.name || data.fullName || payload.name || payload.Name || '';
          this.panGender = data.Gender || data.gender || payload.gender || payload.Gender || '';
          
          let panDobStr = '';
          // Fallback parsing from results array if name is missing
          if (payload.results && Array.isArray(payload.results)) {
            if (!this.panName) {
              const nameIdx = payload.results.findIndex((r: string) => r && r.toLowerCase && r.toLowerCase().trim() === 'name');
              if (nameIdx >= 0 && nameIdx + 1 < payload.results.length) {
                this.panName = payload.results[nameIdx + 1];
              }
            }
            
            const dobIdx = payload.results.findIndex((r: string) => r && r.toLowerCase && (r.toLowerCase().includes('date of birth') || r.toLowerCase().includes('dob')));
            if (dobIdx >= 0 && dobIdx + 1 < payload.results.length) {
              panDobStr = payload.results[dobIdx + 1];
            }
          }
          this.panDob = panDobStr;

          this.panVerified = true;
          
          const resToSave = JSON.parse(JSON.stringify(res));
          if (resToSave?.data?.PhotoBase64) {
             resToSave.data.PhotoBase64 = '';
          }
          if (resToSave?.data?.data?.PhotoBase64) {
             resToSave.data.data.PhotoBase64 = '';
          }

          // Save to KYC and ApplicationDetails
          this.saveKycToTable({
            panNumber: this.panNumber,
            panName: this.panName,
            panGender: this.panGender,
            panDob: this.parseDate(panDobStr),
            isPanVerified: 1,
            panXML: JSON.stringify(resToSave)
          });
          this.saveAppDetail('PAN Number', this.panNumber);
          this.saveAppDetail('PAN Name', this.panName);
          this.saveAppDetail('PAN Verified', '1');
          this.toastr.success('PAN document extracted and saved');
        } else {
          this.toastr.warning('No data extracted from PAN document');
        }
        input.value = '';
      },
      error: (err) => {
        this.isExtractingPan = false;
        this.toastr.error('Failed to extract PAN document');
        console.error(err);
        input.value = '';
      }
    });
  }

  // ---------- Aadhaar Document Upload ----------
  onAadhaarDocumentUpload(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;
    const file = input.files[0];
    this.isExtractingAadhaar = true;
    this.documentExtractionService.extractDocument(file, 'AADHAAR', 'Y').subscribe({
      next: (res) => {
        this.isExtractingAadhaar = false;
        if (res && res.data) {
          const payload = res.data;
          const data = payload.data || {};
          
          this.aadhaarNumber = data.AadhaarNumber || data.aadhaarNumber || payload.aadhaarNumber || this.aadhaarNumber;
          this.aadhaarResponseData = {
            documentType: 'Aadhaar',
            name: data.Name || data.name || data.fullName || payload.name || payload.Name || '',
            dateOfBirth: data.Dob || data.dob || data.dateOfBirth || payload.dob || payload.dateOfBirth || '',
            gender: data.Gender || data.gender || payload.gender || payload.Gender || '',
            careOf: data.CareOf || data.careOf || payload.careOf || '',
            maskedAadhaar: null,
            house: data.House || data.house || payload.house || '',
            street: data.Street || data.street || payload.street || '',
            district: data.District || data.district || payload.district || '',
            subDistrict: data.SubDistrict || data.subDistrict || payload.subDistrict || '',
            landmark: data.Landmark || data.landmark || payload.landmark || '',
            locality: data.Locality || data.locality || payload.locality || '',
            postOfficeName: data.PostOffice || data.postOffice || payload.postOffice || '',
            state: data.State || data.state || payload.state || '',
            pincode: data.Pincode || data.pincode || payload.pincode || '',
            country: 'India',
            vtcName: data.VTC || data.vtc || payload.vtc || '',
            mobile: data.Mobile || data.mobile || payload.mobile || '',
            email: data.Email || data.email || payload.email || '',
            photoBase64: data.PhotoBase64 || data.photoBase64 || payload.photoBase64 || ''
          };
          this.fullAddress = [
            this.aadhaarResponseData.house,
            this.aadhaarResponseData.street,
            this.aadhaarResponseData.landmark,
            this.aadhaarResponseData.locality,
            this.aadhaarResponseData.vtcName,
            this.aadhaarResponseData.postOfficeName,
            this.aadhaarResponseData.district,
            this.aadhaarResponseData.state,
            this.aadhaarResponseData.pincode
          ].filter(Boolean).join(', ');
          this.aadhaarVerified = true;
          
          // Remove photo before stringifying to avoid payload size limits
          const resToSave = JSON.parse(JSON.stringify(res));
          if (resToSave?.data?.PhotoBase64) {
             resToSave.data.PhotoBase64 = '';
          }
          if (resToSave?.data?.data?.PhotoBase64) {
             resToSave.data.data.PhotoBase64 = '';
          }

          // Save to KYC and ApplicationDetails
          this.saveKycToTable({
            aadharNumber: this.aadhaarNumber,
            isAadharVerified: 1,
            name: this.aadhaarResponseData.name,
            gender: this.aadhaarResponseData.gender,
            dateOfBirth: this.parseDate(this.aadhaarResponseData.dateOfBirth),
            careOf: this.aadhaarResponseData.careOf,
            fullAddress: this.fullAddress,
            state: this.aadhaarResponseData.state,
            district: this.aadhaarResponseData.district,
            subDistrict: this.aadhaarResponseData.subDistrict,
            vtc: this.aadhaarResponseData.vtcName,
            street: this.aadhaarResponseData.street,
            house: this.aadhaarResponseData.house,
            landmark: this.aadhaarResponseData.landmark,
            postOffice: this.aadhaarResponseData.postOfficeName,
            pincode: this.aadhaarResponseData.pincode,
            country: this.aadhaarResponseData.country,
            mobile: this.aadhaarResponseData.mobile,
            email: this.aadhaarResponseData.email,
            photo: '', // Don't save huge base64 into the table photo column
            aadhaarXML: JSON.stringify(resToSave)
          });
          this.saveAppDetail('Aadhaar Number', this.aadhaarNumber);
          this.saveAppDetail('Aadhaar Name', this.aadhaarResponseData.name);
          this.saveAppDetail('Aadhaar Verified', '1');
          this.toastr.success('Aadhaar document extracted and saved');
        } else {
          this.toastr.warning('No data extracted from Aadhaar document');
        }
        input.value = '';
      },
      error: (err) => {
        this.isExtractingAadhaar = false;
        this.toastr.error('Failed to extract Aadhaar document');
        console.error(err);
        input.value = '';
      }
    });
  }

  userKycData: UserKYCDTO | null = null;
  applicationData: LoanApplicationItem | null = null;

  // PAN
  panNumber = '';
  panVerified = false;
  panName = '';
  panDob = '';
  panGender = '';
  isLoadingPan = false;

  // Aadhaar
  aadhaarNumber = '';
  aadhaarOtp = '';
  aadhaarOtpSent = false;
  aadhaarVerified = false;
  aadhaarResponseData: Scoreme_AadhaarOtpVerificationResponseDataObject | null = null;
  isLoadingOtp = false;
  isLoadingVerify = false;

  // CKYC
  ckycNumber = '';
  ckycFound = false;

  // Computed address
  fullAddress = '';

  ngOnInit(): void {
    this.losState.userKycData$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(data => {
        this.userKycData = data;
        if (data) this.prefillData(data);
      });

    this.losState.applicationData$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(app => this.applicationData = app);

    this.losState.applicationDetails$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(details => {
        if (details && details.length > 0) {
          const aadharNo = details.find(d => d.parameterName === 'Aadhaar Number')?.value;
          const aadharNm = details.find(d => d.parameterName === 'Aadhaar Name')?.value;
          const aadharVer = details.find(d => d.parameterName === 'Aadhaar Verified')?.value;
          const panNo = details.find(d => d.parameterName === 'PAN Number')?.value;
          const panNm = details.find(d => d.parameterName === 'PAN Name')?.value;
          const panVer = details.find(d => d.parameterName === 'PAN Verified')?.value;

          if (aadharNo) this.aadhaarNumber = this.formatAadhaar(aadharNo);
          if (aadharNm) {
            if (!this.aadhaarResponseData) {
              this.aadhaarResponseData = {
                documentType: 'Aadhaar',
                name: aadharNm,
                dateOfBirth: '',
                gender: '',
                careOf: '',
                maskedAadhaar: null,
                house: '',
                street: '',
                district: '',
                subDistrict: '',
                landmark: '',
                locality: '',
                postOfficeName: '',
                state: '',
                pincode: '',
                country: 'India',
                vtcName: '',
                mobile: '',
                email: '',
                photoBase64: ''
              };
            } else {
              this.aadhaarResponseData.name = aadharNm;
            }
          }
          if (aadharVer === '1') this.aadhaarVerified = true;
          if (panNo) this.panNumber = panNo;
          if (panNm) this.panName = panNm;
          if (panVer === '1') this.panVerified = true;
        }
      });
  }

  startKycProcess(): void {
    // Verification now occurs via document upload.
    // This method is retained for compatibility but does not trigger any API calls.
    this.toastr.info('KYC verification handled by document upload.');
  }

  get canProceed(): boolean {
    return true; // TODO: have to remove it after ocelot issue fix. 
    return this.panVerified && this.aadhaarVerified;
  }

  private get actionUser(): string {
    return this.userLoginService.User()?.userId?.toString() || '0';
  }

  // ─── Prefill ───────────────────────────────────────────────────────
  prefillData(data: UserKYCDTO): void {
    this.panNumber = data.panNumber || '';
    this.panName = data.panName || data.name || '';
    this.panVerified = data.isPanVerified === 1;
    this.panGender = data.panGender || '';

    if (data.aadharNumber) {
      this.aadhaarNumber = this.formatAadhaar(data.aadharNumber);
      this.aadhaarVerified = data.isAadharVerified === 1;
    }

    if (this.aadhaarVerified) {
      this.fullAddress = data.fullAddress || '';
      // Reconstruct display data from saved KYC
      this.aadhaarResponseData = {
        documentType: 'Aadhaar',
        name: data.name || '',
        dateOfBirth: data.dateOfBirth?.toString() || '',
        gender: data.gender || '',
        careOf: data.careOf || '',
        maskedAadhaar: null,
        house: data.house || '',
        street: data.street || '',
        district: data.district || '',
        subDistrict: data.subDistrict || '',
        landmark: data.landmark || '',
        locality: data.vtc || '',
        postOfficeName: data.postOffice || '',
        state: data.state || '',
        pincode: data.pincode || '',
        country: data.country || 'India',
        vtcName: data.vtc || '',
        mobile: data.mobile || '',
        email: data.email || '',
        photoBase64: data.photo || ''
      };
    }
  }

  private formatAadhaar(val: string): string {
    const digits = val.replace(/\D/g, '').slice(0, 12);
    return digits.replace(/(\d{4})(?=\d)/g, '$1 ');
  }

  onAadhaarChange(val: string): void {
    this.aadhaarNumber = this.formatAadhaar(val);
  }

  // ─── PAN Verification ──────────────────────────────────────────────
// Legacy fetchPAN method has been disabled. Use document upload for PAN extraction.
// fetchPAN(){
//   // Deprecated – original API call removed.
// }

  // ─── Aadhaar OTP ───────────────────────────────────────────────────
// Legacy sendAadhaarOtp method disabled – use document upload for Aadhaar extraction.
// sendAadhaarOtp(): void {
//   // Deprecated – original OTP flow removed.
// }

// Legacy verifyAadhaar method disabled – use document upload for Aadhaar extraction.
// verifyAadhaar(): void {
//   // Deprecated – original OTP verification removed.
// }

  // ─── CKYC Search ───────────────────────────────────────────────────
  searchCKYC(): void {
    // TODO: integrate real CKYC API when available
    this.ckycFound = false;
    this.toastr.info('CKYC search — no records found');
  }

  // ─── Proceed to next step ──────────────────────────────────────────
  async proceedNext(): Promise<void> {
    if (!this.canProceed) {
      this.toastr.warning('Please verify both PAN and Aadhaar before proceeding');
      return;
    }

    // Update application status to KYCVerified
    if (this.applicationData && this.applicationId) {
      try {
        const updatePayload: LoanApplicationUpdateDTO = {
          applicationId: this.applicationData.applicationId,
          userId: this.applicationData.userId,
          productId: this.applicationData.productId,
          applicationStatus: 'KYCVerified',
          requestedAmount: this.applicationData.requestedAmount,
          approvedAmount: this.applicationData.approvedAmount,
          tenureMonths: this.applicationData.tenureMonths,
          applicationDate: this.applicationData.applicationDate,
          approvalDate: this.applicationData.approvalDate,
          approvedBy: this.applicationData.approvedBy || '',
          rejectionReason: this.applicationData.rejectionReason || '',
          rejectedBy: this.applicationData.rejectedBy || '',
          isDisbursed: this.applicationData.isDisbursed,
          disbursedOn: this.applicationData.disbursedOn,
          disbursedAmount: this.applicationData.disbursedAmount,
          subUserId: this.applicationData.subUserId,
          businessId: this.applicationData.businessId,
          productCode: this.applicationData.productCode || '',
          prrn: this.applicationData.prrn,
          lmsApplicationName: this.applicationData.lmsApplicationName,
          otherDetail1: this.applicationData.otherDetail1,
          otherDetail2: this.applicationData.otherDetail2,
          otherDetail3: this.applicationData.otherDetail3,
          otherDetail4: this.applicationData.otherDetail4,
          otherDetail5: this.applicationData.otherDetail5,
          otherDetail6: this.applicationData.otherDetail6,
          isActive: 1,
          actionUser: this.actionUser
        };
        const updated = await firstValueFrom(this.loanApplicationService.LoanApplication_Update(updatePayload));
        this.losState.patchApplication(updated);
      } catch (err) {
        console.error('Failed to update status to KYCVerified:', err);
      }
    }

    this.next.emit();
  }

  // ─── Helpers ───────────────────────────────────────────────────────

  /** Persist partial KYC fields to UserKYC table via update. */
  private saveKycToTable(partialData: Partial<UserKYCUpdateRequestDTO>): void {
    const base = this.userKycData;
    if (!base) return;

    const payload: UserKYCUpdateRequestDTO = {
      kycId: base.kycId,
      userId: base.userId,
      careOf: base.careOf || '',
      relation: base.relation || '',
      fullAddress: base.fullAddress || '',
      dateOfBirth: base.dateOfBirth || null,
      yearOfBirth: base.yearOfBirth || 0,
      email: base.email || '',
      mobile: base.mobile || '',
      gender: base.gender || '',
      name: base.name || '',
      aadharNumber: base.aadharNumber || '',
      panNumber: base.panNumber || '',
      photo: base.photo || '',
      shareCode: base.shareCode || '',
      country: base.country || '',
      state: base.state || '',
      district: base.district || '',
      subDistrict: base.subDistrict || '',
      vtc: base.vtc || '',
      street: base.street || '',
      house: base.house || '',
      landmark: base.landmark || '',
      postOffice: base.postOffice || '',
      pincode: base.pincode || '',
      isAadharVerified: base.isAadharVerified || 0,
      isPanVerified: base.isPanVerified || 0,
      monthlyIncome: base.monthlyIncome || 0,
      occupationType: base.occupationType || '',
      employmentType: base.employmentType || '',
      aadhaarXML: base.aadhaarXML || '',
      panXML: base.panXML || '',
      panName: base.panName || '',
      panAddress: base.panAddress || '',
      panGender: base.panGender || '',
      aadhaarLinked: base.aadhaarLinked || 0,
      panDob: base.panDob || null,
      otherDetail1: base.otherDetail1 || '',
      otherDetail2: base.otherDetail2 || '',
      otherDetail3: base.otherDetail3 || '',
      otherDetail4: base.otherDetail4 || '',
      otherDetail5: base.otherDetail5 || '',
      otherDetail6: base.otherDetail6 || '',
      isActive: 1,
      actionUser: this.actionUser,
      ...partialData
    };

    this.userKYCService.UserKYC_Update(payload).subscribe({
      next:  () => console.log('[KYC] UserKYC table updated'),
      error: (err) => console.error('[KYC] Failed to update UserKYC table:', err)
    });
  }

  /** Safely parse dates from extraction API */
  private parseDate(dateStr: string): Date | null {
    if (!dateStr) return null;
    const parts = dateStr.split(/[\/\-]/);
    if (parts.length === 3) {
      if (parts[0].length <= 2 && parts[2].length === 4) {
        return new Date(+parts[2], +parts[1] - 1, +parts[0]);
      }
      if (parts[0].length === 4) {
        return new Date(+parts[0], +parts[1] - 1, +parts[2]);
      }
    }
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? null : d;
  }

  /** Persist a single parameter to ApplicationDetails. */
  private async saveAppDetail(param: string, value: string): Promise<void> {
    if (!this.applicationId) return;
    try {
      await firstValueFrom(this.applicationDetailsService.ApplicationDetails_UpdateByParameterName({
        applicationId: this.applicationId,
        parameterName: param,
        value: value,
        isVerified: 1,
        verificationSource: 'KYC_Gateway',
        actionUser: this.actionUser
      }));
      this.losState.refreshDetails(this.applicationId);
    } catch (err) {
      console.error(`[KYC] Failed to save ${param}:`, err);
    }
  }
}
